import { matchProducts } from '../api/products';

let lineItemIdCounter = 0;

/**
 * Identity for a line item, stable for as long as the line exists. Used as the
 * React key and to address a line in state, so a row's props never depend on
 * its position in the array: inserting or removing a line leaves every other
 * line's props untouched and only the lines that changed re-render.
 */
export const nextLineItemId = () => {
  lineItemIdCounter += 1;
  return `line-${lineItemIdCounter}`;
};

// Fields that linking a line to an existing item overwrites with that item's
// values. They are snapshotted on link so unlinking can put back exactly what
// the user had before, and an unlinked line can never keep the identity (SKU,
// barcodes, name) of an item it no longer points at.
const LINKED_FIELDS = [
  'search',
  'sku',
  'name',
  'unit',
  'barcodes',
  'categoryId',
  'categoryName',
  'productType',
  'unitCost',
  'sellingPrice',
];

const pickLinkedFields = (line) =>
  LINKED_FIELDS.reduce((acc, field) => {
    acc[field] = line[field];
    return acc;
  }, {});

/**
 * Snapshot to store on a line when it is linked to an existing item, so the
 * link can be undone. For a line that is born linked (a scan that adds its own
 * line) pass a blank line: undoing the link there leaves an empty line.
 */
export const snapshotBeforeLink = (line) => pickLinkedFields(line);

/**
 * Patch for typing in the search box of a line with nothing linked: what is
 * typed is also the line's barcode, so the previous search value is replaced by
 * the new one in `barcodes`. Barcodes added any other way are left alone.
 */
export const searchChangePatch = (line, search) => {
  const previous = String(line.search || '').trim();
  const next = String(search || '').trim();
  const kept = line.barcodes.filter((code) => code !== previous);
  return {
    search,
    barcodes: next && !kept.includes(next) ? [...kept, next] : kept,
  };
};

/**
 * The line without the barcode that only mirrors its search box. Picking a
 * product writes that product's SKU into the search box first, and it must not
 * survive in the snapshot that unlinking restores.
 */
export const withoutSearchBarcode = (line) => {
  const search = String(line.search || '').trim();
  return { ...line, barcodes: line.barcodes.filter((code) => code !== search) };
};

/**
 * Patch that undoes a link: the values from before the link, with the search
 * box cleared. `blankLine` is used for a line linked before a snapshot existed.
 * A line with nothing linked only gets its search box cleared, since its fields
 * hold what the user typed.
 */
export const unlinkPatch = (line, blankLine) =>
  line.selectedItem
    ? {
        ...pickLinkedFields(line.beforeLink ?? blankLine),
        search: '',
        selectedItem: null,
        beforeLink: null,
        skuConflict: null,
      }
    : { search: '' };

/**
 * Existing items whose SKU is already used by one of the lines that is NOT
 * linked, keyed by line index. Such a line would create a new item, and a SKU
 * is unique per business, so it has to be linked or given a different SKU.
 * Linked lines are ignored: they add to the item they point at, so carrying
 * that item's SKU is expected.
 *
 * Throws if the lookup fails, so callers surface the problem instead of
 * importing lines whose SKU may already be taken.
 */
export const findSkuConflicts = async (items, businessUuid) => {
  const unlinked = items
    .map((item, idx) => ({ idx, sku: String(item.sku || '').trim() }))
    .filter(({ idx, sku }) => sku && !items[idx].selectedItem);
  if (unlinked.length === 0) return new Map();

  const matches = await matchProducts(
    unlinked.map(({ sku }) => sku),
    [],
    [],
    businessUuid,
  );
  const bySku = new Map();
  (Array.isArray(matches) ? matches : []).forEach((match) => {
    if (match?.sku) bySku.set(String(match.sku).trim().toLowerCase(), match);
  });

  return unlinked.reduce((conflicts, { idx, sku }) => {
    const match = bySku.get(sku.toLowerCase());
    if (match) conflicts.set(idx, match);
    return conflicts;
  }, new Map());
};
